import { useCallback, useState } from "react";

import { LeadContextView } from "./LeadContextTabs";

interface UseChatViewEntryResult {
	view: LeadContextView;
	/** True only for a Chat view entered from a pinned research fact. */
	autoOpenGenerateBar: boolean;
	/** Enter the Chat view, optionally with the AI generate panel already open. */
	openChatView: (withGenerateBar: boolean) => void;
	/** User-driven view switch (the Lead Panel | Chat strip). */
	changeView: (next: LeadContextView) => void;
	/** Host-driven reset, e.g. when the panel switches to a different lead. */
	resetView: (next: LeadContextView) => void;
}

/**
 * Lead Panel | Chat view state for the lead panels, plus the one-shot flag that
 * tells {@link LeadChatTab} to open its AI generate panel on arrival (PREC-3680).
 *
 * The flag is deliberately set only by {@link openChatView} — every other way into
 * the Chat view clears it, so the panel opens exactly when the user came from a
 * pinned fact's "In sequence" chip and never surprises them afterwards.
 */
export const useChatViewEntry = (
	initialView: LeadContextView = "lead-panel"
): UseChatViewEntryResult => {
	const [view, setView] = useState<LeadContextView>(initialView);
	const [autoOpenGenerateBar, setAutoOpenGenerateBar] = useState(false);

	const openChatView = useCallback((withGenerateBar: boolean) => {
		setAutoOpenGenerateBar(withGenerateBar);
		setView("chat");
	}, []);

	const changeView = useCallback((next: LeadContextView) => {
		setAutoOpenGenerateBar(false);
		setView(next);
	}, []);

	return { view, autoOpenGenerateBar, openChatView, changeView, resetView: changeView };
};
